<template>
  <!-- prettier-ignore -->
  <div :class="classes">
    <label>
      <div class="input__title">{{ autoTitle }}</div>

      <div
        ref="codeElementsRef"
        class="input-elements"
      >
        <div
          v-for="(value, index) in values"
          :key="index"
          class="input-element"
        >
          <input
            :key="`code-${index}`"
            :ref="references[index]"
            autocomplete="off"
            v-bind="$attrs"
            :data-id="index"
            :pattern="numeric ? '[0-9]' : null"
            :required="isRequired"
            :type="numeric ? 'tel' : 'text'"
            :value="value"
            maxlength="1"
            placeholder="0"
            v-on="$attrs"
            @focus="$event.target.select()"
            @input="handleChange"
            @keyup.backspace.prevent="remove"
            @keyup.down.prevent
            @keyup.up.prevent
            @keyup.left.prevent="select(index - 1)"
            @keyup.right.prevent="select(index + 1)"
          />
        </div>
      </div>

      <div class="input__hint">{{ autoHint }}</div>
    </label>
    <slot />
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const $emit = defineEmits(['update:modelValue', 'completed'])

// Hooks
const { t } = useI18n()

// Props
const props = defineProps({
  code: {
    type: [String, Number],
    default: null
  },

  hint: {
    type: String,
    default: ''
  },

  length: {
    type: Number,
    default: 4
  },

  modelValue: {
    type: [String, Number],
    default: undefined
  },

  numeric: {
    type: Boolean,
    default: false
  },

  required: {
    type: Boolean,
    default: false
  },

  theme: {
    type: String,
    default: 'light'
  },

  title: {
    type: String,
    default: ''
  },

  validate: {
    type: Object,
    default: () => {}
  }
})


// Data
const values = reactive([])
const codeElementsRef = ref(null)

// Computed
const autoTitle = computed(() => {
  return `${props.title} ${isRequired.value ? '*' : ''}`
})

const autoHint = computed(() => {
  const errors = Object.keys(props.validate?.$params ?? {}).filter(error => !props.validate[error])

  if (invalid.value && errors.length > 0) {
    return t(`form.error.${errors[0]}`)
  } else {
    return props.hint
  }
})

const classes = computed(() => {
  return {
    'input': true,
    'input--invalid': invalid.value,
    [`input--${props.theme}`]: true
  }
})

const invalid = computed(() => {
  return props.validate?.$invalid && props.validate?.$error
})

const isRequired = computed(() => {
  return props.required || props.validate?.required !== undefined
})

const references = computed(() => {
  const referencesArray = []
  for (let i = 0; i < props.length; i++) {
    referencesArray.push(`input_${i}`)
  }
  return referencesArray
})


// Methods
function emitUpdate() {
  //this.values = values
  let code = values.join('')

  if (code === '') {
    code = null
  }

  $emit('update:modelValue', code)

  if (code?.length >= props.length) {
    props.validate?.$touch()
    $emit('completed', code)
  }
}

function handleChange($event) {
  if (props.numeric) {
    $event.target.value = $event.target.value.replace(/[^\d]/gi, '')
  }
  if ($event.target.value === '' || (props.numeric && !$event.target.validity.valid)) {
    return
  }

  let nextIndex
  const index = parseInt($event.target.dataset.id)
  const value = $event.target.value

  if (value.length <= 1) {
    nextIndex = index + 1
    values[index] = value
  } else {
    const split = value.split('')
    nextIndex = (nextIndex >= props.length ? props.length : value.length + index) - 1

    split.forEach((item, i) => {
      const cursor = index + i
      if (cursor < props.length) {
        values[cursor] = item
      }
    })
  }

  select(nextIndex)
  emitUpdate()
}

function initializeValues() {
  const code = props.modelValue?.toString()
  if (code?.length) {
    for (let i = 0; i < props.length; i++) {
      values.push(code[i] || null)
    }
  } else {
    Array(props.length).fill(null).forEach(item => {
      values.push(item)
    })
  }

  nextTick(() => select(0))
}

function remove($event) {
  const index = parseInt($event.target.dataset.id)
  const nextIndex = values[index] ? index : references.value[index - 1] ? index - 1 : null

  if (references.value[nextIndex]) {
    values[nextIndex] = null
    select(nextIndex)
    emitUpdate()
  }
}

function select(key) {
  if (codeElementsRef.value.children[key]) {
    const el = codeElementsRef.value.children[key].children[0]

    el.focus()
    el.select()

    // @todo: why is this reset needed?
    props.validate?.$reset()
 }
}

// Events
onMounted(() => {
  initializeValues()
})

</script>

<style name="mobile" scoped>
.input {
  position: relative;
  width: 100%;

  &__title {
    font-size: 15px;
    line-height: 16px;
    color: #999999;
    padding: 4px 10px;
    z-index: 1;
    cursor: text;
  }

  &-element {
    display: inline-block;
    margin: 0 5px 0 0;
    cursor: text;
    border-bottom: 1px solid var(--c-black);
    transition: border-bottom-color .2s ease-in-out;

    &:last-child {
      margin: 0;
    }
  }

  &-element input {
    width: 50px;
    height: 50px;
    font-size: 20px;
    text-align: center;
    color: var(--c-black);
    background: none;
    border: none;
    outline: none;
    border-bottom: 1px solid transparent;
    transition: border-bottom .2s ease-in-out;

    &::-ms-clear {
      display: none;
    }

    &:focus {
      border-bottom: 1px solid var(--c-black);
    }
  }

  &__hint {
    font-size: 15px;
    line-height: 20px;
    color: #999999;
    padding: 4px 10px;

    transition: color 0.2s ease-in-out;
  }

  &--invalid {
    .input-element, .input-element input:focus {
      border-bottom-color: var(--c-error);
    }

    .input__title,
    .input__hint {
      color: var(--c-error);
    }
  }

  &--dark {
    .input-element {
      background-color: var(--c-warm-gray);
    }
  }

  &--light {
    .input-element {
      background-color: var(--c-white);
    }
  }
}
</style>
